import React, { useRef, useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Send, CheckCircle } from "lucide-react";
import { publicApi } from "../../lib/api";
import { toast } from "sonner";

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [threshold]);
  return [ref, isVisible];
};

export const ContactFormSection = () => {
  const [ref, isVisible] = useInView(0.05);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please enter your name and message");
      return;
    }
    setSending(true);
    try {
      await publicApi.sendMessage(form);
      setSent(true);
      setForm({ name: "", phone: "", email: "", message: "" });
      toast.success("Message sent! We'll get back to you soon.");
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact-form" ref={ref} className="py-24 bg-slate-50/50 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-14 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="text-sm font-semibold text-sky-500 tracking-wider uppercase">Get in Touch</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">Send Us a Message</h2>
          <p className="text-slate-500 mt-4 max-w-2xl mx-auto">Have a question or want to discuss a project? Drop us a message and we'll get back to you shortly.</p>
        </div>

        <div className="max-w-xl mx-auto">
          <Card className={`border border-sky-100/50 shadow-lg shadow-sky-100/10 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <CardContent className="p-6 sm:p-8">
              {sent ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Message Sent!</h3>
                  <p className="text-slate-500 text-sm">Thank you for reaching out. We'll get back to you soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-slate-700">Name *</Label>
                      <Input
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder="Your name"
                        required
                        className="border-slate-200 focus:border-sky-400"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-slate-700">Phone</Label>
                      <Input
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                        placeholder="Your phone number"
                        className="border-slate-200 focus:border-sky-400"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-700">Email</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="your@email.com"
                      className="border-slate-200 focus:border-sky-400"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-slate-700">Message *</Label>
                    <Textarea
                      value={form.message}
                      onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                      placeholder="Tell us about your project or ask a question..."
                      rows={4}
                      required
                      className="border-slate-200 focus:border-sky-400"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={sending}
                    className="w-full h-11 bg-sky-500 hover:bg-sky-600 text-white rounded-lg font-semibold shadow-md shadow-sky-200/50 transition-all duration-300"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sending ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
